export const formatCnpj = (value: number | string): string => {
  if (!value) return '';

  if (String(value)?.length < 12) return String(value);

  const cnpj = ('000000000' + value).substr(-14);

  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const formatDecimalPlaces = (value: number, precision = 2) => {
  return value.toLocaleString('pt-BR', {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
  });
};

const formatThousandMask = (value: number, precision = 2): string => {
  const absoluteNumber = Math.abs(value);

  if (absoluteNumber >= 1e3 && absoluteNumber < 1e6)
    return `${formatDecimalPlaces(value / 1e3)} mil`;

  if (absoluteNumber >= 1e6 && absoluteNumber < 1e9)
    return `${formatDecimalPlaces(value / 1e6)} mi`;

  if (absoluteNumber >= 1e9 && absoluteNumber < 1e12)
    return `${formatDecimalPlaces(value / 1e9)} bi`;

  if (absoluteNumber >= 1e12) return `${formatDecimalPlaces(value / 1e12)} tri`;

  return `${formatDecimalPlaces(value, precision)}`;
};

interface IFormatNumber {
  type?: 'percent' | 'money' | 'percentage';
  thousandMask?: boolean;
  precision?: number;
  hideZero?: boolean;
  useNullPlaceholder?: boolean;
  currency?: 'BRL' | 'USD' | 'EUR';
}

export const formatNumber = (
  value: number | string,
  {
    type,
    thousandMask,
    precision = 2,
    hideZero = false,
    useNullPlaceholder = false,
    currency = 'BRL',
  }: IFormatNumber = {}
) => {
  const nullPlaceholder = '-';

  if (
    useNullPlaceholder &&
    (value === null || value === undefined || isNaN(Number(value)))
  ) {
    return nullPlaceholder;
  }

  let numberValue = Number(value);
  numberValue = type && type === 'percentage' ? numberValue * 100 : numberValue;
  let formattedValue = formatDecimalPlaces(numberValue, precision);

  if (hideZero) {
    formattedValue = formattedValue.replace(/\,00+$/, '');
  }

  if (type && (type === 'percent' || type === 'percentage')) {
    return `${formattedValue}%`;
  }

  if (type && type === 'money') {
    if (thousandMask) {
      // Usa Intl para pegar o símbolo da moeda dinamicamente
      const parts = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).formatToParts(0);

      const symbol = parts.find((p) => p.type === 'currency')?.value || '';
      const valueWithThousand = formatThousandMask(numberValue, precision);

      return hideZero
        ? `${symbol} ${valueWithThousand}`.replace(',00', '')
        : `${symbol} ${valueWithThousand}`;
    } else {
      const formatter = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
      });
      const valueWithThousand = formatter.format(numberValue) ?? formattedValue;
      return `${valueWithThousand}`;
    }
  }

  if (thousandMask) {
    formattedValue = formatThousandMask(numberValue, precision);
    return hideZero ? formattedValue.replace(/\,00+$/, '') : formattedValue;
  }

  return formattedValue;
};

export const truncateText = (text: string, truncate: number = 30) => {
  if (!text) return text;

  return truncate && text.length > truncate
    ? `${text.slice(0, truncate)}...`
    : text;
};

export const convertInPercentage = (value: number): number => {
  return value * 100;
};

export const capitalyzeText = (value: string): string => {
  if (!value) return value;

  const numberVerify = /^[a-zA-Z]/;
  const isFirstLetterText = numberVerify.test(value);

  if (isFirstLetterText) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  return value;
};

export const formatCreditCard = (value: string): string => {
  if (value) {
    const digits = 4;
    const separator = ' ';

    const onlyNumbers = value.replace(/\D/g, '');

    const blocks: string[] = [];
    let block = '';
    for (let i = 0; i < onlyNumbers.length; i++) {
      block += onlyNumbers[i];
      if ((i + 1) % digits === 0) {
        blocks.push(block);
        block = '';
      }
    }
    const formattedNumber = blocks.join(separator);

    return formattedNumber;
  }
  return value;
};

export const formatValidDate = (value: string): string => {
  if (value) {
    const onlyNumber = value.replace(/\D/g, '');

    const month = onlyNumber.substring(0, 2);
    const year = onlyNumber.substring(2);

    const formattedDate = `${month}/${year}`;

    return formattedDate;
  }
  return value;
};

export function formatPhoneNumber(number: string) {
  if (!number) return '';

  const cleaned = number.replace(/\D/g, '');

  if (cleaned.length === 11) {
    // Celular
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (cleaned.length === 10) {
    // Fixo
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  } else {
    return number; // formato não esperado
  }
}

export function extractPhoneNumberDigits(formatted: string | null | undefined): number {
  // Handle null, undefined, or non-string inputs
  if (!formatted || typeof formatted !== 'string') {
    throw new Error('Número de telefone é obrigatório');
  }
  
  const cleaned = formatted.replace(/\D/g, '');
  
  // Handle empty string (no digits found)
  if (cleaned === '') {
    throw new Error('Número de telefone deve conter ao menos um dígito');
  }
  
  const result = parseInt(cleaned, 10);
  
  
  return result;
}
